import * as React from "react";
import { Selection, type IColumn } from "@fluentui/react";
import type { VehicleGroupAssignmentSortKey, VehicleGroupAssignment } from "../../api";
import { createUuid, type UUID } from "../../utils/uuid";
import { observer } from "mobx-react";
import { action, computed, makeObservable } from "mobx";
import { omit } from "ramda";
import {
    type VehicleGroupAssignmentsApiResource,
    type VehicleGroupAssignmentsQuery,
} from "../../domain/repositories/repository-vehicle-group-assignments";
import type { SortState } from "../../utils/sort-state";
import type { PaginationState } from "../../utils/pagination-state";
import { LayoutList } from "../layouts/layout-list";
import { ElofleetList, type ElofleetListProps } from "../atoms/elofleet-list";
import type { SelectionState } from "../../utils/selection-state";
import { defaultPageSize } from "../../utils/constants";

export interface ListVehicleGroupAssignmentProps
    extends Omit<ElofleetListProps, "items" | "columns"> {
    readonly ids?: UUID[];
    readonly vehicleId?: UUID;
    readonly vehicleGroupId?: UUID;
    readonly showVehicle?: boolean;
    readonly showGroup?: boolean;
    readonly commandBar?: JSX.Element;
    readonly listStateId?: UUID;
}

interface ListVehicleGroupAssignmentItem {
    readonly key: UUID;
    readonly vehicle: string;
    readonly groupLabel: string;
    readonly createdAt: string;
}

import { store } from "../../store";

@observer
export class ListVehicleGroupAssignments extends React.Component<ListVehicleGroupAssignmentProps> {
    private _listStateId = createUuid();
    private selection = new Selection({
        onSelectionChanged: () => this.updateSelection(),
    });

    constructor(props: ListVehicleGroupAssignmentProps) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        store.serviceListStates.vehicleGroupAssignments.initializeList(this.listStateId, {
            query: () => this.query,
        });
    }

    @computed private get listStateId(): UUID {
        return this.props.listStateId ?? this._listStateId;
    }

    @computed private get sortState(): SortState<VehicleGroupAssignmentSortKey> {
        return store.serviceListStates.vehicleGroupAssignments.getSortState(this.listStateId);
    }

    @computed private get paginationState(): PaginationState<VehicleGroupAssignmentsApiResource> {
        return store.serviceListStates.vehicleGroupAssignments.getPaginationState(this.listStateId);
    }

    @computed private get selectionState(): SelectionState {
        return store.serviceListStates.vehicleGroupAssignments.getSelectionState(this.listStateId);
    }

    @computed private get query(): VehicleGroupAssignmentsQuery {
        const { vehicleId } = this.props;
        return { vehicleId, pageSize: defaultPageSize, ...this.sortState.query };
    }

    @computed private get vehicleGroupAssignments(): (VehicleGroupAssignment | undefined)[] {
        if (this.props.ids) {
            return this.props.ids.map((id) => store.repositoryVehicleGroupAssignments.byId(id));
        }
        return store.repositoryVehicleGroupAssignments.byQuery(
            this.query,
            this.paginationState.pagination,
        );
    }

    @computed private get items(): (ListVehicleGroupAssignmentItem | undefined)[] {
        return this.vehicleGroupAssignments.map((vehicleGroupAssignment) => {
            if (!vehicleGroupAssignment) {
                return;
            }
            return {
                key: vehicleGroupAssignment.id,
                vehicle: store.repositoryVehicles.byId(vehicleGroupAssignment.vehicleId)?.id ?? "",
                groupLabel:
                    store.repositoryVehicleGroups.byId(vehicleGroupAssignment.vehicleGroupId)
                        ?.label ?? "",
                createdAt: store.i18n.formatDateOnly(vehicleGroupAssignment.createdAt),
            };
        });
    }

    @action.bound private updateSelection(): void {
        // Update the listState's selectionState to the new selected keys.
        this.selectionState.updateSelection(
            this.selection.getSelection().map((item) => item.key as UUID),
        );
    }

    public render(): JSX.Element {
        if (!store.serviceListStates.vehicleGroupAssignments.isInitialized(this.listStateId)) {
            return <></>;
        }
        // Forward properties that are used by Fluent UI to the `ShimmeredDetailsList`.
        const props = omit(
            ["ids", "vehicleId", "vehicleGroupId", "showVehicle", "showGroup"],
            this.props,
        );
        const columns: IColumn[] = [];
        if (this.props.showVehicle ?? true) {
            columns.push({
                fieldName: "vehicle",
                name: store.i18n.t("listVehicleGroupAssignments.column.vehicle.name"),
                key: "username",
                minWidth: 100,
            });
        }
        if (this.props.showGroup ?? true) {
            columns.push({
                fieldName: "groupLabel",
                name: store.i18n.t("listVehicleGroupAssignments.column.vehicleGroup.name"),
                key: "groupLabel",
                minWidth: 100,
            });
        }
        columns.push({
            fieldName: "createdAt",
            name: store.i18n.t("listVehicleGroupAssignments.column.createdAt.name"),
            key: "createdAt",
            minWidth: 100,
        });
        return (
            <LayoutList paginationState={this.paginationState} commandBar={this.props.commandBar}>
                <ElofleetList
                    {...props}
                    selection={this.selection}
                    onColumnHeaderClick={this.sortState.toggleColumn}
                    columns={this.sortState.patchColumns(columns)}
                    items={this.items}
                />
            </LayoutList>
        );
    }
}
